import { ILocalDate } from "../../../entities/calendar";
import { IStaffShiftPattern, IUpdateStaffShiftPatternRequest } from "../../../entities/shift";

export interface IStaffShiftPatternForm {
    anchorDate: string;
    workDays: string;
    restDays: string;
    startTime: string;
    endTime: string;
    effectiveFrom: string;
    effectiveTo: string;
    shiftPrice: string;
}

interface IValidationResult {
    data?: Omit<IUpdateStaffShiftPatternRequest, 'userId'>;
    error?: string;
}

const getLocalDateFromDate = (date: Date): ILocalDate => ({
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    day: date.getDate(),
})

const getDateInputValue = (date?: ILocalDate): string => {
    if(!date?.year || !date.month || !date.day) return ''

    return `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`
}

const getLocalDateFromInput = (value: string): ILocalDate | null => {
    const [year, month, day] = value.split('-').map(Number)

    if(!year || !month || !day) return null

    return {year, month, day}
}

const getSlotTimeLabel = (slotIndex: number): string => {
    if(slotIndex === 48) return '24:00'

    const minutes = slotIndex * 30
    const hours = Math.floor(minutes / 60)
    const restMinutes = minutes % 60

    return `${String(hours).padStart(2, '0')}:${String(restMinutes).padStart(2, '0')}`
}

const getSlotFromTime = (value: string): number | null => {
    const [hoursRaw, minutesRaw] = value.split(':')
    const hours = Number(hoursRaw)
    const minutes = Number(minutesRaw)

    if(Number.isNaN(hours) || Number.isNaN(minutes)) return null
    if(hours === 24 && minutes === 0) return 48
    if(hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return null
    if(minutes % 30 !== 0) return null

    return hours * 2 + minutes / 30
}

export const getDefaultStaffShiftPatternForm = (): IStaffShiftPatternForm => {
    const today = getDateInputValue(getLocalDateFromDate(new Date()))

    return {
        anchorDate: today,
        workDays: '2',
        restDays: '2',
        startTime: '08:00',
        endTime: '22:00',
        effectiveFrom: today,
        effectiveTo: '',
        shiftPrice: '0',
    }
}

export const getStaffShiftPatternForm = (pattern: IStaffShiftPattern): IStaffShiftPatternForm => ({
    anchorDate: getDateInputValue(pattern.anchorDate),
    workDays: String(pattern.workDays || 2),
    restDays: String(pattern.restDays || 2),
    startTime: getSlotTimeLabel(pattern.startSlot),
    endTime: getSlotTimeLabel(pattern.startSlot + pattern.durationSlots),
    effectiveFrom: getDateInputValue(pattern.effectiveFrom),
    effectiveTo: getDateInputValue(pattern.effectiveTo),
    shiftPrice: pattern.shiftPrice || '0',
})

export const validateStaffShiftPatternForm = (form: IStaffShiftPatternForm): IValidationResult => {
    const anchorDate = getLocalDateFromInput(form.anchorDate)
    const effectiveFrom = getLocalDateFromInput(form.effectiveFrom)
    const effectiveTo = form.effectiveTo ? getLocalDateFromInput(form.effectiveTo) : undefined
    const workDays = Number(form.workDays)
    const restDays = Number(form.restDays)
    const startSlot = getSlotFromTime(form.startTime)
    const endSlot = getSlotFromTime(form.endTime)

    if(!anchorDate) return {error: 'Укажите опорную дату'}
    if(!effectiveFrom) return {error: 'Укажите дату начала действия графика'}
    if(form.effectiveTo && !effectiveTo) return {error: 'Проверьте дату окончания действия графика'}
    if(!Number.isInteger(workDays) || workDays < 1 || workDays > 31) return {error: 'Рабочих дней должно быть от 1 до 31'}
    if(!Number.isInteger(restDays) || restDays < 1 || restDays > 31) return {error: 'Выходных дней должно быть от 1 до 31'}
    if(startSlot === null || endSlot === null) return {error: 'Время указываем с шагом 30 минут, например 08:00 или 22:30'}
    if(startSlot === 48 || endSlot <= startSlot) return {error: 'Время окончания должно быть позже времени начала'}
    return {
        data: {
            anchorDate,
            workDays,
            restDays,
            startSlot,
            durationSlots: endSlot - startSlot,
            effectiveFrom,
            effectiveTo: effectiveTo ?? undefined,
            shiftPrice: form.shiftPrice.trim() || '0',
        }
    }
}
